import React, { RefObject, useEffect } from 'react';
import ResizeObserver from 'resize-observer-polyfill';

import { theme } from '$/components/Layout/theme';
import {
  InteractionType,
  MyRole,
  ResultsAppearAt,
  useDeleteInteractionMutation,
} from '$/GraphQL/schema/schema';
import { css } from '@emotion/react';

import NoInteraction from '$/components/Interactions/cards/NoInteractionsCard';
import {
  InteractionDisplayState,
  InteractionInfo,
} from '$/components/Interactions/InteractionInfo';
import { ModalDeleteButton } from '$/components/ui/Button/Button';
import { DragOrCloseHandle } from '$/components/ui/DragOrCloseHandle';
import { useThemeContext } from '$/components/ui/ThemeContext';
import {
  staticWebinarDimensions,
  WebinarDimensions,
  WebinarPlayerContext,
  WebinarPlayerUIState,
  withWebinarPlayerContext,
} from '$/components/WebinarPlayer/context';
import { HasJoined, PlayerMode, PlayerPlacement } from '$/components/WebinarPlayer/types';
import {
  GET_EWEBINAR_INTERACTIONS_IN_SET,
  GET_INTERACTION_TEMPLATE_TYPES,
} from '$/GraphQL/queries/Interactions';
import { useHasChangedShallow, usePrevious } from '$/hooks/useHasChanged';
import useRunOnce from '$/hooks/useRunOnce';
import { OpenPaneIcon } from '$/images/FontAwesomeIcons';
import { EditIcon } from '$/images/Icons';
import EDate from '$/shared/frontend/utils/EDate';
import isMobile from '$/utils/isMobile';
import { LocalTimeZone } from '$/utils/LocalTimeZone';
import { logger } from '$/utils/logger';
import { useOnWindowResize } from '$/utils/useWindowSize';
import {
  animate,
  AnimatePresence,
  AnimationPlaybackControls,
  motion,
  MotionValue,
  PanInfo,
  transform,
  useDragControls,
  useMotionValue,
  useTransform,
  ValueAnimationTransition,
} from 'framer-motion';
import { navigate } from 'gatsby';
import tinycolor from 'tinycolor2';
import tw from 'twin.macro';
import AttendeeCard from '../../Interactions/cards/AttendeeCard';
import {
  InteractionsStreamContext,
  InteractionsStreamContextProvider,
  ScrollToCardFn,
} from './context';
import { DockIcon, DockIconHeight, DockIconTopPx } from './DockIcon';
import { ScoreSpinner } from './ScoreSpinner';
import { ConditionalDisplayIcon } from '$/components/Interactions/ConditionalDisplayIcon';
import { InteractionCardsReverseScroll } from '../WebinarPlayer';
import { withContextValue } from '$/hooks/withPartialContexts';
import { WaitFor } from '$/components/DelayRender';
import { useRefFromState } from '$/hooks/useRefFromState';
import { Portal } from '$/components/Portal';
import normalizeWheel from 'normalize-wheel';
import { useDebouncedCallback } from 'use-debounce';
import { useServerContext } from '$/hooks/useServerDateNow';
import { isAndroid } from 'react-device-detect';
import { useEmbeddedPlayerContext } from '$/components/EmbeddedPlayer/useEmbeddedPlayer';
import Button from '$/components/ui/Button';
import { Popup } from '$/components/ui/Popup';
import { DocumentNode } from 'graphql';
import { useMe } from '$/hooks/useMe';

export function transformRange(n: number, from: [number, number], to: [number, number]): number {
  const range = to[0] + ((to[1] - to[0]) / (from[1] - from[0])) * (n - from[0]);
  return range;
}

export function useCardParallax(
  _interactionId: string,
  refPlaceholder: RefObject<HTMLDivElement>,
  scrollTop: MotionValue<number>,
  interactionHeightsVersion: MotionValue<number>,
  extraTopSpacing: MotionValue<number> | undefined,
  dimensions: {
    inEdit: boolean;
    isHidden: boolean;
    focusedCardMode: boolean;
    cardHeight: number;
    nextCardHeight: number;
    cardWidth: number;
    index: number;
  },
  dimensionsRef: React.MutableRefObject<WebinarDimensions>,
  cardInteractionsRef: React.MutableRefObject<InteractionInfo[]>
): {
  y: MotionValue<number>;
  scale: MotionValue<number>;
  opacity: MotionValue<number>;
  bodyOpacity: MotionValue<number>;
  rotateX: MotionValue<number>;
  headerHeight: MotionValue<number>;
  filter: MotionValue<string>;
  visibility: MotionValue<string>;
} {
  const defaultExtraTopSpacing = useMotionValue<number>(0);
  const snap = dimensionsRef.current.actionsPane.snappedCardTopPx;
  const y = useTransform<number, number>(
    [scrollTop, interactionHeightsVersion, extraTopSpacing ?? defaultExtraTopSpacing],
    ([top, _ihv, removingInteractionHeight]) => {
      let snap = dimensionsRef.current.actionsPane.snappedCardTopPx;

      if (dimensions.focusedCardMode || dimensionsRef.current.actionsPane.location === 'right') {
        const availableHeight =
          dimensionsRef.current.actionsPane.heightPx +
          (dimensionsRef.current.actionsPane.location === 'bottom'
            ? dimensionsRef.current.actionsPane.actionsTopPx
            : -dimensionsRef.current.actionsPane.actionsTopPx) -
          snap -
          dimensionsRef.current.actionsPane.icard.marginYpx;
        const missingHeight = dimensions.cardHeight - availableHeight;
        if (missingHeight > 0) {
          snap = -missingHeight;
        }
      }

      const y = -top + (refPlaceholder.current?.offsetTop ?? 0) + removingInteractionHeight;

      const snappedCardTopPx = dimensionsRef.current.actionsPane.snappedCardTopPx;
      if (y > snap || dimensions.inEdit) return y;

      if (y >= snappedCardTopPx - dimensions.cardHeight) {
        return transformRange(
          y,
          [snap, snappedCardTopPx - dimensions.cardHeight],
          [snap, snappedCardTopPx / 2]
        );
      }
      return Math.max(
        0,
        transformRange(
          y,
          [
            snappedCardTopPx - dimensions.cardHeight,
            snappedCardTopPx - dimensions.cardHeight - dimensions.nextCardHeight,
          ],
          [snappedCardTopPx / 2, 0]
        )
      );

      /*
    if (y < minTop - headerTravelHeight) return containerTop;
    if (y < minTop)
      return transformRange(y, [minTop, minTop - headerTravelHeight], [topPx, containerTop]);
    return transformRange(y, [topPx, minTop], [topPx, topPx]);
    */
    }
  );
  const visibility = useTransform<number, string>(scrollTop, (top) => {
    if (dimensions.index >= cardInteractionsRef.current.length - 3) {
      // don't hide last 3 cards
      return 'visible';
    }
    const y = -top + (refPlaceholder.current?.offsetTop ?? 0);
    const visible =
      /* Unclear why we need this 400px buffer or bottom cards disappear on overscroll */
      y + 400 >
      snap -
        dimensions.cardHeight -
        dimensions.nextCardHeight -
        dimensionsRef.current.actionsPane.heightPx;
    return visible ? 'visible' : 'hidden';
  });
  const rotateX = useTransform(scrollTop, (top) => {
    const y = -top + (refPlaceholder.current?.offsetTop ?? 0);

    if (y > snap - dimensions.cardHeight / 2) return 0;

    return transform(
      y,
      [
        snap - dimensions.cardHeight - dimensions.nextCardHeight,
        snap - dimensions.cardHeight,
        snap - dimensions.cardHeight / 2,
      ],
      [-30, -15, 0]
    );
  });
  const headerHeight = useTransform(scrollTop, (top) => {
    const y = -top + (refPlaceholder.current?.offsetTop ?? 0);
    const normalHeight =
      dimensions.cardWidth * staticWebinarDimensions.actionsPane.icard.mediaTitleRatio;
    if (dimensions.isHidden) {
      return normalHeight;
    }
    return transform(y, [snap - dimensions.cardHeight, snap], [45, normalHeight]);
  });
  const scale = useTransform(scrollTop, (top) => {
    const y = -top + (refPlaceholder.current?.offsetTop ?? 0);
    return transform(y, [snap - dimensions.cardHeight, snap], [0.975, 1]);
  });
  const opacity = useTransform(scrollTop, (_top) => {
    return 1;

    /*
    const y = -top + (refPlaceholder.current?.offsetTop ?? 0);
    if (y < snap - dimensions.cardHeight - dimensions.nextCardHeight - 50)
      return transformRange(
        y,
        [
          snap - dimensions.cardHeight - dimensions.nextCardHeight - 50,
          snap - dimensions.cardHeight - dimensions.nextCardHeight - 100,
        ],
        [1, 0]
      );
    return 1;*/
  });
  const filter = useTransform(scrollTop, (top) => {
    const y = -top + (refPlaceholder.current?.offsetTop ?? 0);

    if (y > snap) return `saturate(1)`;
    return `saturate(${transformRange(
      y,
      [snap, snap - dimensions.cardHeight - dimensions.nextCardHeight],
      [1, 0]
    )}`;
  });
  const bodyOpacity = useTransform(scrollTop, (_top) => {
    return 1;
    /*
    const y = -top + (refPlaceholder.current?.offsetTop ?? 0);

    if (y > snap - dimensions.cardHeight + 20) return 1;
    return transformRange(
      y,
      [snap - dimensions.cardHeight + 20, snap - dimensions.cardHeight],
      [1, 0]
    );
    */
  });
  return React.useMemo(
    () => ({
      y,
      rotateX,
      headerHeight,
      scale,
      opacity,
      filter,
      bodyOpacity,
      visibility,
    }),
    [bodyOpacity, filter, headerHeight, opacity, rotateX, scale, visibility, y]
  );
}

const EditCardHeader: React.FC<{
  interaction: InteractionInfo;
  onClickEdit: (i: InteractionInfo) => void;
}> = withWebinarPlayerContext(
  ['set', 'setPlaybackPosition', 'isPlaying', 'setWillBeRemovedCard'],
  'EditCardHeader'
)(({ interaction, onClickEdit, context }) => {
  const { set, setPlaybackPosition, isPlaying, setWillBeRemovedCard } = context;

  const [deleteInteraction, deleteInteractionResult] = useDeleteInteractionMutation();

  const me = useMe();
  const refetchQueries = React.useMemo(() => {
    const queries: {
      query: DocumentNode;
      variables?: {
        ewebinarSetId: string;
        includeDraft: boolean;
        includePublished: boolean;
        includeLegacyInteractions: boolean;
      };
    }[] = [
      {
        query: GET_EWEBINAR_INTERACTIONS_IN_SET,
        variables: {
          ewebinarSetId: set.id,
          includeDraft: true,
          includePublished: false,
          includeLegacyInteractions: false,
        },
      },
    ];
    if (me.inTeam.role === MyRole.Ops) {
      queries.push({
        query: GET_INTERACTION_TEMPLATE_TYPES,
      });
    }
    return queries;
  }, [set.id, me.inTeam.role]);

  const onDelete = React.useCallback(async () => {
    if (!interaction.userCanAdd() || deleteInteractionResult.loading) {
      return;
    }

    try {
      interaction.id && setWillBeRemovedCard(interaction.id, true);
      await deleteInteraction({
        variables: {
          id: interaction.id!,
        },
        refetchQueries,
      });
    } catch (e) {
      interaction.id && setWillBeRemovedCard(interaction.id, false);
      logger.error('Error deleting interaction', e);
    }
  }, [
    deleteInteraction,
    deleteInteractionResult.loading,
    interaction,
    setWillBeRemovedCard,
    refetchQueries,
  ]);

  if (isPlaying) return null;

  return (
    <div
      className={`flex font-body justify-between text-black cursor-pointer mt-1_5e mb-3/4e pointer-events-auto w-full`}
    >
      <div
        className='text-sm'
        css={css`
          .time {
            ${tw`text-blue-3`}
          }
          &:hover {
            .time {
              ${tw`text-black`}
            }
          }
        `}
        onClick={(): void => {
          setPlaybackPosition(interaction.playbackPosition);
        }}
      >
        {interaction.typeName()}
        <span className='time'> at {interaction.appearAtPlaybackPosString()}s</span>
      </div>
      <div className='flex flex-grow items-center justify-end'>
        {interaction.userCanAdd() && (
          <>
            <ConditionalDisplayIcon interaction={interaction} />
            <ModalDeleteButton
              confirmClick
              onClick={onDelete}
              submitting={deleteInteractionResult.loading}
              className='button-control h-1e w-1e'
            />

            <Button.icon
              className='ml-1e'
              onClick={(): void => onClickEdit(interaction)}
              icon={<EditIcon className='h-1e w-1e' />}
            />
          </>
        )}
      </div>
    </div>
  );
});

interface AttendeeCardPageProps {
  pageHeight: number;
  cardHeight: number;
  nextCardHeight: number;
  cardsListHeight: number;
  actionsPaneIcardWidthRem: number;
  cardDisplayState: InteractionDisplayState;
  extraTopSpacing: MotionValue<number>;
  snappedIdRef: React.RefObject<string | undefined>;
  scrollToCard: ScrollToCardFn;

  containerRef: RefObject<HTMLDivElement>;
  cardsScrollContainerRef: RefObject<HTMLDivElement>;
  scrollTop: MotionValue<number>;
  interaction: InteractionInfo;
  nextInteraction: InteractionInfo | undefined;
  index: number;
  onClickEdit: (interaction: InteractionInfo) => void;
}

const AttendeeCardPage = withContextValue<
  WebinarPlayerUIState,
  AttendeeCardPageProps,
  {
    pageHeight: number;
    cardHeight: number;
    nextCardHeight: number;
    actionsPaneIcardWidthRem: number;
    cardDisplayState: InteractionDisplayState;
    extraTopSpacing?: MotionValue<number>;
  }
>(
  WebinarPlayerContext,
  (
    { interactionHeights, dimensions, isPlaying, mode, playbackPosition },
    { interaction, nextInteraction }
  ) => {
    const { removingInteractionHeight, removingInteraction } =
      React.useContext(InteractionsStreamContext);
    const isEditAndPaused = mode === PlayerMode.edit && !isPlaying;
    let cardHeight = interactionHeights[interaction.shortId] ?? 300;
    if (interaction.type === InteractionType.EndStream) {
      cardHeight = 60;
    }
    const pageHeight =
      cardHeight +
      dimensions.actionsPane.icard.marginYpx +
      (isEditAndPaused ? 40 /* Edit Header Height */ : 0);

    // scroll all cards below the one being removed
    let extraTopSpacing = undefined;
    if (removingInteraction) {
      if (removingInteraction.playbackPosition < interaction.playbackPosition) {
        extraTopSpacing = removingInteractionHeight;
      }

      // same playback position, check id
      if (
        removingInteraction.playbackPosition === interaction.playbackPosition &&
        removingInteraction.id &&
        interaction.id &&
        Number(removingInteraction.id) < Number(interaction.id)
      ) {
        extraTopSpacing = removingInteractionHeight;
      }
    }
    return {
      pageHeight,
      cardHeight,
      nextInteraction:
        nextInteraction && interactionHeights[nextInteraction.shortId]
          ? nextInteraction
          : undefined,
      nextCardHeight: nextInteraction ? interactionHeights[nextInteraction.shortId] ?? 0 : 0,
      actionsPaneIcardWidthRem: dimensions.actionsPane.icard.widthRem,
      cardDisplayState: interaction.cardDisplayState(playbackPosition, mode),
      extraTopSpacing,
    };
  },
  'AttendeeCardPage'
)(
  withWebinarPlayerContext(
    [
      'mode',
      'isPlaying',
      'setInteractionHeight',
      'interactionHeightsVersion',
      'ewebinar',
      'actionsPaneState',
      'focusedCardMode',
      'dimensionsRef',
      'cardInteractionsRef',
      'willBeRemovedCards',
    ],
    'AttendeeCardPage'
  )((props) => {
    const {
      interaction,
      nextInteraction,
      scrollTop,
      index,
      snappedIdRef,
      onClickEdit,
      pageHeight,
      cardHeight,
      nextCardHeight,
      actionsPaneIcardWidthRem,
      cardDisplayState,
      extraTopSpacing,
      scrollToCard,
      context: {
        mode,
        isPlaying,
        setInteractionHeight,
        interactionHeightsVersion,
        ewebinar,
        actionsPaneState,
        focusedCardMode,
        dimensionsRef,
        cardInteractionsRef,
        willBeRemovedCards,
      },
    } = props;

    const refPlaceholder = React.useRef<HTMLDivElement>(null);
    const shadowColor = tinycolor(ewebinar.primaryColor).toHsl();
    shadowColor.s = 10;
    shadowColor.l = 60;

    const isEditAndPaused = mode === PlayerMode.edit && !isPlaying;
    const [cardWidth, setCardWidth] = React.useState<number>();
    const cardDomRef = React.useRef<HTMLDivElement>();
    const cardWidthRef = React.useRef(cardWidth);

    const parallaxStyle = useCardParallax(
      interaction.shortId,
      refPlaceholder,
      scrollTop,
      interactionHeightsVersion,
      extraTopSpacing,
      {
        inEdit: isEditAndPaused,
        isHidden: actionsPaneState !== 'shown',
        cardHeight,
        cardWidth: cardWidth ?? staticWebinarDimensions.actionsPane.icard.widthRem * 14,
        nextCardHeight,
        index,
        focusedCardMode,
      },
      dimensionsRef,
      cardInteractionsRef
    );

    // wait for the card width updating to reduce calling setInteractionHeight
    React.useLayoutEffect(() => {
      if (!cardWidth) return;
      setTimeout(() => {
        if (!cardDomRef.current) return;
        cardWidthRef.current = cardWidth;
        setInteractionHeight(interaction.shortId, cardDomRef.current.clientHeight);
      }, 100);
    }, [cardWidth, cardWidthRef, interaction.shortId, setInteractionHeight]);

    const cardDisplayStateChanged = useHasChangedShallow([cardDisplayState], { skipInitial: true });
    React.useLayoutEffect(() => {
      if (!cardDomRef.current || !cardDisplayStateChanged) return;
      if (
        interaction.id &&
        willBeRemovedCards[interaction.id] &&
        interaction.fragment.details?.resultsAppearAt !== ResultsAppearAt.Immediately
      ) {
        scrollToCard(interaction, true);
        return;
      }
      logger.debug('ANIM', 'cardDisplayState changed - set new height', {
        shortId: interaction.shortId,
        cardDisplayState,
        nextCardHeight: cardDomRef.current.clientHeight,
      });
      setInteractionHeight(interaction.shortId, cardDomRef.current.clientHeight, true);
    }, [
      scrollToCard,
      cardDisplayState,
      cardDisplayStateChanged,
      interaction,
      setInteractionHeight,
      willBeRemovedCards,
    ]);

    const cardObserver = React.useRef(
      new ResizeObserver((entries) => {
        // Only care about the first element, we expect one element to be watched
        if (cardWidthRef.current) {
          setInteractionHeight(interaction.shortId, entries[0].target.clientHeight);
        }
      })
    );

    const refCard = React.useCallback(
      (node: HTMLDivElement) => {
        if (!node) return;
        cardDomRef.current = node;
        setCardWidth(node.clientWidth);
        const observer = cardObserver.current;
        observer.observe(node);

        return (): void => {
          observer.unobserve(node);
        };
      },
      [cardObserver]
    );

    const card = React.useMemo(() => {
      return isEditAndPaused ? (
        <motion.div
          css={css`
            position: absolute;
            top: 0;
            width: ${actionsPaneIcardWidthRem}rem;
          `}
          style={{ y: parallaxStyle.y }}
        >
          <EditCardHeader interaction={interaction} onClickEdit={onClickEdit} />
          <AttendeeCard
            cardRef={refCard}
            snappedIdRef={snappedIdRef}
            interaction={interaction}
            nextInteraction={nextInteraction}
            css={css`
              position: static !important;
            `}
          />
        </motion.div>
      ) : (
        <>
          <AttendeeCard
            cardRef={refCard}
            interaction={interaction}
            snappedIdRef={snappedIdRef}
            style={parallaxStyle}
            nextInteraction={nextInteraction}
            css={css`
              position: absolute;
              top: 0;
              z-index: ${1000 + index};
            `}
          />
        </>
      );
    }, [
      isEditAndPaused,
      actionsPaneIcardWidthRem,
      parallaxStyle,
      interaction,
      onClickEdit,
      refCard,
      snappedIdRef,
      index,
      nextInteraction,
    ]);

    return (
      <motion.div
        ref={refPlaceholder}
        className={`
          attendee-card-page
          acp-${interaction.shortId}
          iid-${interaction.id}
          ${interaction.type}
        `}
        css={[
          tw`flex flex-col items-center w-full`,
          css`
            align-items: flex-start;
            pointer-events: none;
            background: transparent;
            color: #fff;
            background-dbg: ${theme.colors.rainbow[
              (index % Object.keys(theme.colors.rainbow).length) + 1
            ].toString()};
          `,
          css`
            height: ${pageHeight}px;
          `,
        ]}
      >
        {card}
      </motion.div>
    );
  })
);

const InteractionsStreamCards: React.FC<{
  className?: string;
  dataLoaded?: boolean;
  snappedIdRef: React.RefObject<string | undefined>;
  snapPointAndInteractionFor: (
    point: number
  ) => { interaction: InteractionInfo; top: number } | null;
  snapToClosestCard: (fromPoint?: number, timeout?: number) => void;
  scrollTop: MotionValue<number>;
  scrollingTo: React.MutableRefObject<number>;
  scrollToCard: ScrollToCardFn;
  scrollToIcon: (interaction: InteractionInfo, options?: { instant?: boolean }) => void;
  nonCardInteractions: InteractionInfo[];
  cardInteractions: InteractionInfo[];
  cardsListRef: React.RefObject<HTMLDivElement>;
  cardsScrollContainerRef: React.RefObject<HTMLDivElement>;
  onClickCardEdit: (interaction: InteractionInfo) => void;
}> = withWebinarPlayerContext(
  [
    'dimensions',
    'actionsPaneState',
    'isPlaying',
    'editMode',
    'totalReactions',
    'interactionHeights',
    'focusedCardMode',
    'setFocusedCardMode',
    'animations',
    'transitions',
  ],
  'InteractionsStreamCards'
)(({
  className,
  dataLoaded,
  snapToClosestCard,
  snapPointAndInteractionFor,
  scrollTop,
  scrollingTo,
  scrollToIcon,
  cardInteractions,
  nonCardInteractions,
  snappedIdRef,
  cardsListRef,
  cardsScrollContainerRef,
  scrollToCard,
  onClickCardEdit,
  context: {
    totalReactions,
    dimensions,
    actionsPaneState,
    isPlaying,
    editMode,
    interactionHeights,
    focusedCardMode,
    setFocusedCardMode,
    animations,
    transitions,
  },
}) => {
  const isEditAndPaused = editMode && !isPlaying;
  const dragControls = useDragControls();
  const interactionsStreamRef = React.useRef<HTMLDivElement>(null);

  const onScroll = React.useCallback(
    (delta: number, option?: { velocity?: number; dontSnap?: boolean }): void => {
      if (!cardsListRef.current || !cardsScrollContainerRef.current) return;

      const minTop = -dimensions.actionsPane.snappedCardTopPx; // Overscroll gutter
      const maxTop =
        cardsListRef.current.scrollHeight -
        dimensions.actionsPane.snappedCardTopPx -
        Math.max(
          0,
          (interactionHeights[cardInteractions[cardInteractions.length - 1].shortId] ?? 0) -
            dimensions.actionsPane.snappedCardTopPx
        ) +
        30; // Overscroll gutter

      const newTop = Math.round(Math.max(minTop, Math.min(maxTop, -scrollTop.get() + delta)));

      logger.debug('ANIM', 'onScroll(', delta, ',', option?.velocity, ')', {
        scrollingTo: scrollingTo.current,
        newTop,
        scrollHeight: cardsListRef.current.scrollHeight,
        maxTop,
      });
      if (scrollingTo.current === newTop) {
        // Call snap so it knows we're still actively scrolling...
        if (!option?.dontSnap) snapToClosestCard();

        // Skip actual scroll since we're going to the same target as we're at
        // This happens when we scroll past the edge w/ the wheel
        return;
      }

      logger.debug('ANIM', 'Scrolling to ', newTop);
      scrollingTo.current = newTop;
      if (option?.velocity) {
        animate(scrollTop, -newTop, { type: 'spring', velocity: option?.velocity });
      } else {
        scrollTop.set(-newTop);
      }

      if (!option?.dontSnap) snapToClosestCard(undefined, 300);
    },
    [
      cardsListRef,
      cardsScrollContainerRef,
      dimensions.actionsPane.snappedCardTopPx,
      interactionHeights,
      cardInteractions,
      scrollTop,
      scrollingTo,
      snapToClosestCard,
    ]
  );

  const onCardsWheel = React.useCallback<React.WheelEventHandler<HTMLDivElement>>(
    (e) => {
      if ((e.target as HTMLDivElement)?.closest?.('.menu.visible')) {
        // scrolling in the dropdown menu - don't scroll the interactions stream
        return;
      }
      if (interactionsStreamRef.current?.style.pointerEvents === 'none') {
        // don't scroll the interactions stream if the next webinar card is open
        return;
      }
      // Slow scroll by 20% & set scrolling speed limits
      const event = normalizeWheel(e);
      onScroll(Math.max(-90, Math.min(90, event.pixelY * 0.8)));
    },
    [onScroll]
  );

  const snapToScrollTopPosition = React.useCallback(() => {
    const snapResults = snapPointAndInteractionFor(scrollTop.get());

    if (!snapResults) {
      return;
    }

    scrollToIcon(snapResults.interaction);
  }, [scrollToIcon, scrollTop, snapPointAndInteractionFor]);

  const onCardsDrag = React.useCallback<
    (event: MouseEvent | TouchEvent | PointerEvent, info: PanInfo) => void
  >(
    (_e) => {
      snapToScrollTopPosition();
    },
    [snapToScrollTopPosition]
  );

  const onCardsDragStart = React.useCallback<
    (event: MouseEvent | TouchEvent | PointerEvent, info: PanInfo) => void
  >(
    (e, info) => {
      if ((e.target as HTMLDivElement)?.closest?.('.menu.visible')) {
        // scrolling in the dropdown menu - don't scroll the interactions stream
        // HACK: dragControls.componentControls is a private member
        // https://github.com/framer/motion/issues/363#issuecomment-620710724
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (dragControls as any).componentControls.forEach((entry: any) => {
          entry.stop(e, info);
        });
      }
    },
    [dragControls]
  );

  const onCardsDragEndCapture = React.useCallback(() => {
    snapToScrollTopPosition();
  }, [snapToScrollTopPosition]);

  const negativeScrollTop = useTransform(scrollTop, (top) => -top);

  React.useEffect(() => {
    setFocusedCardMode(false);
  }, [setFocusedCardMode, totalReactions]);

  const onCloseFocusedBg = React.useCallback(() => {
    setFocusedCardMode(false);
  }, [setFocusedCardMode]);

  const hasFocusedCardModeChanged = useHasChangedShallow([focusedCardMode]);
  React.useEffect(() => {
    if (focusedCardMode) {
      logger.debug('ANIM', 'start ', ['cards.focused.on']);
      animations.start(['cards.focused.on']);
    } else {
      logger.debug('ANIM', 'start ', ['cards.focused.off']);
      animations.start(['cards.focused.off']);
    }
  }, [animations, focusedCardMode, hasFocusedCardModeChanged]);

  // START to fix Android issue: broken layout when focusing on the input box Android device
  const onMouseDownElement = React.useCallback(
    (e: MouseEvent): void => {
      if (!isAndroid || focusedCardMode) return;

      if (!(e.target instanceof HTMLElement)) return;
      const target = e.target as HTMLElement;
      const tagName = target.localName;

      // Skip this process when focusing on message input chat or not focus on input and textarea
      if (!['input', 'textarea'].includes(tagName)) return;

      const inputElement = e.target as HTMLInputElement | HTMLTextAreaElement;
      if (inputElement.name === 'message') return;

      e.stopPropagation();
      e.preventDefault();

      setFocusedCardMode(true);
      setTimeout(() => {
        target.focus();
      }, 800);
    },
    [focusedCardMode, setFocusedCardMode]
  );
  React.useEffect(() => {
    if (!isAndroid) return;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    window.addEventListener('mousedown', onMouseDownElement);
    return (): void => window.removeEventListener('mousedown', onMouseDownElement);
  }, [onMouseDownElement]);
  // END - Android issue

  const [cardsListHeight, setCardListHeight] = React.useState(0);
  const cardListObserver = React.useRef(
    new ResizeObserver((entries) => {
      // Only care about the first element, we expect one element to be watched
      if (cardsListRef.current) {
        setCardListHeight(entries[0].target.clientHeight);
      }
    })
  );

  React.useEffect(() => {
    if (!cardsListRef.current) return;
    const observer = cardListObserver.current;
    observer.observe(cardsListRef.current);
  }, [cardsListRef]);

  const cardsList = React.useMemo((): React.ReactNode | null => {
    if (cardInteractions.length === 0) return null;
    return (
      <motion.div
        id='cards-list'
        ref={cardsListRef}
        className={`${editMode && 'edid-mode'}`}
        css={[
          css`
            position: absolute;
            left: 0;
            right: 0;
            top: 0;

            &.edit-mode {
              top: 0.1rem;
            }

            &:before {
              content: '';
              transition-duration: 1s;
              transition-property: flex-basis;
              flex-grow: 0;
              flex-shrink: 0;
              flex-basis: ${dimensions.actionsPane.snappedCardTopPx}px;
            }

            &:after {
              content: '';
            }

            > div {
              &:last-child {
                margin-bottom: 0px !important;
              }
            }
          `,
        ]}
        style={{ y: negativeScrollTop }}
      >
        <AnimatePresence>
          {cardInteractions.map((interaction, index) => (
            <AttendeeCardPage
              key={interaction.shortId}
              index={index}
              snappedIdRef={snappedIdRef}
              cardsScrollContainerRef={cardsScrollContainerRef}
              scrollTop={negativeScrollTop}
              interaction={interaction}
              nextInteraction={cardInteractions[index + 1]}
              containerRef={cardsListRef}
              onClickEdit={onClickCardEdit}
              cardsListHeight={cardsListHeight}
              scrollToCard={scrollToCard}
            />
          ))}
        </AnimatePresence>
      </motion.div>
    );
  }, [
    cardInteractions,
    cardsListRef,
    dimensions.actionsPane.snappedCardTopPx,
    editMode,
    negativeScrollTop,
    onClickCardEdit,
    snappedIdRef,
    cardsScrollContainerRef,
    cardsListHeight,
    scrollToCard,
  ]);

  const snapPointFor = React.useCallback(
    (target: number): number => {
      const result = snapPointAndInteractionFor(target);
      if (!result) {
        return target;
      }
      return result.top;
    },
    [snapPointAndInteractionFor]
  );

  return (
    <InteractionsStreamContextProvider scrollToCard={scrollToCard}>
      <WaitFor element='nonCardInteractions'>
        <div className='hidden'>
          {nonCardInteractions.map((interaction) => (
            <AttendeeCard
              key={interaction.id}
              snappedIdRef={snappedIdRef}
              interaction={interaction}
            />
          ))}
        </div>
      </WaitFor>

      <motion.div
        id='focused-card-bg'
        className={`${isEditAndPaused && 'is-edit'}`}
        ref={cardsScrollContainerRef}
        css={css`
          position: ${isAndroid && focusedCardMode ? 'fixed' : 'absolute'};

          ${tw`z-player-focused-card`}
          bottom: 0;
          left: 0;
          top: 0;
          right: 0;

          display: flex;
          justify-content: center;
          align-items: center;

          pointer-events: none;
          > * {
            pointer-events: all;
          }
        `}
      >
        <motion.div
          css={css`
            position: absolute;
            pointer-events: none;
            top: 0;
            left: 0;
            bottom: 0;
            right: ${dimensions.actionsPane.idock.widthRem}rem;
          `}
          transition={transitions['card.focus']}
          variants={{
            'cards.focused.on': {
              WebkitBackdropFilter: 'blur(10px)',
              backdropFilter: 'blur(10px)',
            },
            'cards.focused.off': {
              WebkitBackdropFilter: 'blur(0px)',
              backdropFilter: 'blur(0px)',
            },
          }}
        />
        <motion.div
          css={css`
            display: none;
            opacity: 0;
          `}
          variants={{
            'cards.focused.on': {
              opacity: 1,
              display: 'flex',
              transition: transitions['card.focus'],
            },
            'cards.focused.off': {
              opacity: 0,
              transition: transitions['card.focus'],
              transitionEnd: {
                display: 'none',
              },
            },
          }}
        >
          <DragOrCloseHandle
            css={css`
              position: absolute;
              top: 0;
              right: ${dimensions.actionsPane.idock.widthRem}rem;
              width: unset;
              left: 0;
              height: 3rem;
              display: flex;
              align-items: center;
              justify-content: center;
              ${tw`z-player-focused-card`}
            `}
            onClose={onCloseFocusedBg}
          />
        </motion.div>
        <motion.div
          id='interaction-stream'
          className={`
              ${className}
              interaction-stream z-player-cards
            `}
          css={css`
            touch-action: none;
            display: flex;
            flex-direction: column;
            align-items: flex-end;
            overflow: visible;
            pointer-events: ${actionsPaneState === 'shown' ? 'all' : 'none'};
            touch-action: none;

            position: absolute;
            top: 0;
            height: calc(
              100vh +
                ${
                  cardsListRef.current?.clientHeight ??
                  0 /* Must always cover screen area so scrolling works in card gaps */
                }px
            );
            right: ${dimensions.actionsPane.idock.widthRem +
            dimensions.actionsPane.idock.paddingLeftRem}rem;
            width: calc(${dimensions.actionsPane.icard.widthRem}rem);
          `}
          animate={{
            top: focusedCardMode
              ? 3 * 14 // Leave room for close handle
              : 0,
            transition: transitions['card.focus'],
          }}
          ref={interactionsStreamRef}
          dragPropagation
          drag='y'
          dragTransition={{
            timeConstant: 500,
            modifyTarget: snapPointFor,
          }}
          onWheel={onCardsWheel}
          onDragStart={onCardsDragStart}
          onDrag={onCardsDrag}
          dragControls={dragControls}
          onDragTransitionEnd={onCardsDragEndCapture}
          style={{ y: scrollTop }}
          transition={isMobile ? transitions['card.focus'] : transitions['video.resize']}
          variants={{
            'actions.overlay.open': { pointerEvents: 'none' },
            'actions.overlay.close': { pointerEvents: 'all' },
          }}
        >
          {cardsList}
          {cardInteractions.length === 0 && dataLoaded && !isPlaying && (
            <NoInteraction isEdit={Boolean(editMode)} />
          )}
        </motion.div>
      </motion.div>

      <WaitFor
        key={`interactionFirstCard-${isEditAndPaused}`}
        element='snapToCardOnPauseOrPlayTrigger'
      />
    </InteractionsStreamContextProvider>
  );
});

const SnapToCardOnPauseOrPlay: React.FC<{
  snappedInteraction?: InteractionInfo;
  scrollToCard: ScrollToCardFn;
}> = withWebinarPlayerContext(['isPlaying', 'editMode', 'cardInteractions'])((props) => {
  const { snappedInteraction, scrollToCard, context } = props;
  const { isPlaying, editMode, cardInteractions } = context;
  const hasIsPlayingChanged = useHasChangedShallow([isPlaying]);
  useEffect(() => {
    // Scroll to the last snapped card since pause/play causes a relayout of cards to inline cards
    if (!hasIsPlayingChanged || !editMode) return;

    if (!snappedInteraction) {
      // First time run - snap to top card
      logger.debug('ANI', 'SnapToFirstCardInEdit - First time run - snap to top card', {
        interaction: cardInteractions[0],
      });
      if (cardInteractions[0]) {
        scrollToCard(cardInteractions[0], false, { duration: 0 });
      }
      return;
    }

    // Give it some time to lay out
    logger.debug('ANI', 'SnapToFirstCardInEdit - Snap to snapped card', {
      interaction: snappedInteraction,
    });
    scrollToCard(snappedInteraction, false, { duration: 0 });
  }, [
    cardInteractions,
    editMode,
    hasIsPlayingChanged,
    isPlaying,
    scrollToCard,
    snappedInteraction,
  ]);

  return null;
});
const InteractionsStreamLoaded = withContextValue<
  WebinarPlayerUIState,
  { className?: string; isLatestNew: boolean },
  { isLatestNew: boolean }
>(
  WebinarPlayerContext,
  ({ playbackPosition, cardInteractions }) => {
    const latestInteraction = InteractionCardsReverseScroll
      ? cardInteractions[0]
      : cardInteractions[cardInteractions.length - 1];
    const isLatestNew = latestInteraction?.playbackPosition === playbackPosition;
    return {
      isLatestNew,
    };
  },
  'InteractionsStreamLoaded'
)(
  withWebinarPlayerContext(
    [
      'ewebinar',
      'markInteractionActive',
      'markInteractionInactive',
      'rooms',
      'currentRoom',
      'scrollCardsToPosition',
      'setPlaybackPosition',
      'playbackPositionRef',
      'isPlaying',
      'mode',
      'editMode',
      'cardInteractions',
      'cardInteractionsRef',
      'nonCardInteractions',
      'dataLoaded',
      'hasJoined',
      'activeInteractionRef',
      'dimensions',
      'dimensionsRef',
      'animations',
      'actionsPaneState',
      'setActionsPaneState',
      'scrollToCardOrPositionTarget',
      'scrollToCardOrPosition',
      'interactionHeights',
      'fastInteractionHeights',
      'focusedCardMode',
      'activeInteractionId',
      'interactionState',
      'placement',
      'showOverlay',
      'isCollapsedEmbeddedPlayer',
      'transitions',
      'showingInVideoFormBeforeWatching',
    ],
    'InteractionsStreamLoaded'
  )(({ className, isLatestNew, context }) => {
    const etheme = useThemeContext();

    const {
      ewebinar,
      markInteractionActive,
      markInteractionInactive,
      rooms,
      currentRoom,
      scrollCardsToPosition,
      setPlaybackPosition,
      playbackPositionRef,
      isPlaying,
      mode,
      placement,
      editMode,
      cardInteractions,
      cardInteractionsRef,
      nonCardInteractions,
      dataLoaded,
      hasJoined,
      activeInteractionRef,
      dimensions,
      dimensionsRef,
      animations,
      actionsPaneState,
      setActionsPaneState,
      scrollToCardOrPositionTarget,
      scrollToCardOrPosition,
      interactionHeights,
      fastInteractionHeights,
      focusedCardMode,
      activeInteractionId,
      interactionState,
      showOverlay,
      transitions,
      showingInVideoFormBeforeWatching,
      isCollapsedEmbeddedPlayer,
    } = context;
    const { isExpanded } = useEmbeddedPlayerContext();
    const isEditAndPaused = editMode && !isPlaying;

    const refDockContainer = React.useRef<HTMLDivElement | null>(null);
    const refDockIcons = React.useRef<HTMLDivElement>(null);
    const cardsListRef = React.useRef<HTMLDivElement>(null);
    const cardsScrollContainerRef = React.useRef<HTMLDivElement>(null);
    const scrollTop = useMotionValue(0);
    const scrollingTo = React.useRef(-1);
    const [dockContainerHeight, setDockContainerHeight] = React.useState<number | undefined>(
      undefined
    );

    const isPlaybackMode = [PlayerMode.live, PlayerMode.replay].includes(mode!);

    const snappedIdRef = React.useRef<string | undefined>();
    const iconsTop = useMotionValue(dimensions.actionsPane.idock.iconsTop);
    const cursorTop = useMotionValue(0);
    const iconsBarAnimateRef = React.useRef<AnimationPlaybackControls | undefined>();

    const scrollDock = React.useCallback(
      (interaction: InteractionInfo, animationOptions?: ValueAnimationTransition<number>) => {
        if (!refDockContainer.current) {
          console.error('No dock scroll');
          return;
        }

        const iconEl = refDockContainer.current.getElementsByClassName(
          'dock-icon-' + interaction.shortId
        )[0] as HTMLDivElement | undefined;

        if (!iconEl) {
          console.error(`Couldn't find ${interaction.id} to scroll to icon`);
          return;
        }

        // const dockHeight = dockScrollRef.current.clientHeight;
        const midOffset =
          refDockContainer.current.clientHeight / 2 -
          dimensionsRef.current.actionsPane.idock.iconsTop;

        if (
          Math.ceil(
            refDockContainer.current.clientHeight + dimensionsRef.current.actionsPane.actionsTopPx
          ) < dimensionsRef.current.videoControlsArea.height
        ) {
          // We're in the middle of a resize transition... wait for it.
          window.setTimeout(() => {
            scrollDock(interaction, animationOptions);
          }, 200);
          return;
        }

        const newTop =
          iconEl.offsetTop > midOffset
            ? midOffset - iconEl.offsetTop
            : dimensionsRef.current.actionsPane.idock.iconsTop;

        if (animationOptions?.duration === 0) {
          iconsTop.set(newTop, true);
        } else {
          iconsBarAnimateRef.current = animate(iconsTop, newTop, {
            type: 'tween',
            duration: 0.5,
            ...(Math.abs(iconsTop.get()) === 1000 ? transitions['card.new'] : {}),
            ...animationOptions,
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
          } as any);
        }
      },
      [dimensionsRef, iconsTop, transitions]
    );

    const scrollDockCursorIntoView = React.useCallback(
      (newTop?: number): void => {
        if (newTop === undefined) {
          newTop = cursorTop.get();
        }

        const dockHeight =
          (refDockContainer.current?.clientHeight ?? 0) -
          (dimensionsRef.current.chat.heightPx + 2 * 14) -
          DockIconHeight; // Reduce by icon height so limit shows the last icon

        // newTop is relative dock-icons.  So we have to compare it to iconsTop to see if it's visible
        if (newTop + iconsTop.get() < dimensionsRef.current.actionsPane.idock.iconsTop) {
          // Cursor is no longer visible.
          // Scroll dock down
          animate(iconsTop, -(newTop - dimensionsRef.current.actionsPane.idock.iconsTop), {
            type: 'tween',
            duration: 0.5,
          });
        }
        if (newTop + iconsTop.get() > dockHeight) {
          // Cursor is no longer visible.
          // Scroll dock up
          animate(
            iconsTop,
            -(
              newTop +
              dimensionsRef.current.actionsPane.actionsTopPx -
              dimensionsRef.current.actionsPane.idock.iconsTop -
              dockHeight
            ),
            {
              type: 'tween',
              duration: 0.5,
            }
          );
        }
      },
      [cursorTop, dimensionsRef, iconsTop]
    );

    const dockIconIdRef = React.useRef<string>();
    const scrollToIcon = React.useCallback(
      (interaction: InteractionInfo, options?: { instant?: boolean }) => {
        (async function (): Promise<void> {
          const id = interaction.id;
          if (!id || !refDockContainer.current) {
            if (!editMode) {
              console.error('No icon scroll');
            }
            return;
          }

          if (dockIconIdRef.current === interaction.id && !options?.instant) {
            logger.debug('ANIM', 'Skip icon scroll');
            return;
          }
          dockIconIdRef.current = interaction.id;

          if (!options?.instant) {
            if (iconsBarAnimateRef.current) {
              iconsBarAnimateRef.current.stop();
            }

            scrollDock(interaction);
          }

          const snappedIndex = cardInteractionsRef.current.findIndex((i) => i.id === id);
          if (snappedIndex === -1) {
            return;
          }
          const newTop = DockIconTopPx + snappedIndex * DockIconHeight;
          if (cursorTop.get() === 0 || options?.instant) {
            cursorTop.set(newTop, true);
          } else {
            animate(cursorTop, newTop, { type: 'tween' });
          }

          scrollDockCursorIntoView(newTop);

          logger.debug('ANIM', `605 start`, ['icon.unsnap.all', `icon.snap.${id}`]);
          await animations.start(['icon.unsnap.all', `icon.snap.${id}`]);
        })();
      },
      [cardInteractionsRef, cursorTop, scrollDockCursorIntoView, animations, editMode, scrollDock]
    );

    const snappedInteraction = cardInteractions.find((i) => i.id === snappedIdRef.current);

    const dockBounds = {
      top: Math.min(0, -(refDockIcons.current?.clientHeight ?? 0) + (dockContainerHeight ?? 0) / 2),
      bottom: dimensions.actionsPane.idock.iconsTop,
    };

    const dockContainerObserver = React.useRef(
      new ResizeObserver((entries) => {
        const clientHeight = entries[0].target.clientHeight;
        if (clientHeight !== dockContainerHeight) {
          setDockContainerHeight(clientHeight);
        }
      })
    );

    const refDockContainerCallback = React.useCallback(
      (node: HTMLDivElement) => {
        if (!node) return;
        refDockContainer.current = node;
        const observer = dockContainerObserver.current;
        observer.observe(node);

        return (): void => {
          observer.unobserve(node);
        };
      },
      [dockContainerObserver]
    );
    const onDockWheel = React.useCallback(
      (e: Pick<React.WheelEvent<HTMLDivElement>, 'deltaX' | 'deltaY'>): void => {
        if (!refDockContainer.current) return;

        logger.debug('ANIM', 'onDockWheel', {
          top: dockBounds.top,
          bottom: dockBounds.bottom,
          currentIconsTop: iconsTop.get(),
        });

        const newTop = Math.max(
          dockBounds.top,
          Math.min(dockBounds.bottom, iconsTop.get() - e.deltaY)
        );

        animate(iconsTop, newTop, { duration: 0.025, type: 'spring' });
      },
      [dockBounds.bottom, dockBounds.top, iconsTop]
    );

    const recalcDockDimensions = React.useCallback(() => {
      const i = snappedInteraction ?? (cardInteractions[0] as InteractionInfo | undefined);
      if (!i) return;

      window.setTimeout(() => {
        scrollDock(i, { duration: 0 });
        scrollToIcon(i, { instant: true });
      }, 0);
    }, [cardInteractions, scrollDock, scrollToIcon, snappedInteraction]);

    const hasDockAreaChanged = useHasChangedShallow([
      dimensions.actionsPane.location, // Add pane location to deal with mobile rotate
      dimensions.webinarArea.height, // Add webinarArea so we recalc on resize
    ]);

    React.useLayoutEffect(() => {
      if (!hasDockAreaChanged) return;
      recalcDockDimensions();
    }, [hasDockAreaChanged, recalcDockDimensions]);

    const setSnapped = React.useCallback(
      async (interaction: InteractionInfo) => {
        if (snappedIdRef.current === interaction.id) {
          return;
        }
        const id = interaction.id;
        logger.debug('ANIM', `setSnapped(${id})`);

        snappedIdRef.current = id;
        scrollToIcon(interaction);
      },
      [scrollToIcon]
    );

    const debounceScrollToIcon = useDebouncedCallback(scrollToIcon, 25, {
      trailing: true,
      leading: false,
    });
    const setSnappedAndDebounceScrollToIcon = React.useCallback(
      async (interaction: InteractionInfo) => {
        if (snappedIdRef.current === interaction.id) {
          return;
        }
        const id = interaction.id;
        logger.debug('ANIM', `setSnapped(${id})`);

        snappedIdRef.current = id;
        debounceScrollToIcon(interaction);
      },
      [debounceScrollToIcon]
    );

    const snappedCardTopPxRef = useRefFromState(dimensions.actionsPane.snappedCardTopPx);
    const scrollToCard: ScrollToCardFn = React.useCallback<ScrollToCardFn>(
      (
        interaction,
        userInitiated,
        transition,
        skipTransition?: boolean,
        skipMarkInteractionInactive?: boolean
      ) => {
        if (userInitiated) {
          // Make sure this card sticks around
          markInteractionActive(interaction);
        } else {
          !skipMarkInteractionInactive && markInteractionInactive();
        }

        logger.debug(
          'ANIM',
          `scrollToCard(${interaction.id},${userInitiated}) snapped: `,
          snappedIdRef.current
        );
        if (!cardsListRef.current) {
          logger.debug('ANIM', 'No card scroll', {
            cardsListRef: cardsListRef.current,
          });
          return;
        }

        if (actionsPaneState !== 'shown' && userInitiated) {
          setActionsPaneState('shown');
          logger.debug('ANIM', '1047 start', ['card.expand.all']);
          animations.start('card.expand.all');
        }

        let newTop = 0;

        const card = cardsListRef.current.getElementsByClassName(`iid-${interaction.id}`)[0] as
          | HTMLDivElement
          | undefined;

        if (!card) {
          logger.error('Scrolling to card not found');
        } else {
          newTop = card.offsetTop;
        }
        newTop -= snappedCardTopPxRef.current;

        setSnapped(interaction);
        if (newTop === -scrollTop.get()) {
          // No scrolling needed
          logger.debug('ANIM', 'Not scrolling ', { to: newTop, scrollTop: scrollTop.get() });
        } else {
          logger.debug('ANIM', 'Scrolling to ', {
            to: newTop,
            scrollTop: scrollTop.get(),
            offsetTop: card?.offsetTop,
            halfCard: card?.clientHeight ?? 0 / 2,
          });

          // Smooth scroll
          if (scrollingTo.current === newTop) {
            return;
          }

          scrollingTo.current = newTop;
          if (skipTransition) {
            scrollTop.set(-newTop);
          } else {
            animate(
              scrollTop,
              -newTop,
              transition ?? {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                ...(transitions['cards.scroll'] as any),
                type: isEditAndPaused ? 'tween' : 'spring',
              }
            );
          }
        }
      },
      [
        actionsPaneState,
        snappedCardTopPxRef,
        setSnapped,
        scrollTop,
        markInteractionActive,
        markInteractionInactive,
        setActionsPaneState,
        animations,
        transitions,
        isEditAndPaused,
      ]
    );

    const haveHeightsChanged = useHasChangedShallow([interactionHeights]);
    React.useEffect(() => {
      if (!haveHeightsChanged) return;

      // A card height changed, so we need to re-calculate the scroll position
      const interaction = cardInteractions.find((i) => i.id === snappedIdRef.current);
      if (interaction) {
        scrollToCard(interaction, false, undefined, false, true);
      }
    }, [cardInteractions, haveHeightsChanged, interactionHeights, scrollToCard, snappedIdRef]);

    const snapTimer = React.useRef(0);
    const icardMarginYpx = dimensions.actionsPane.icard.marginYpx;
    const actionsPaneSnappedCardTopPx = dimensions.actionsPane.snappedCardTopPx;

    const snapPointAndInteractionFor = React.useCallback(
      (target: number): { interaction: InteractionInfo; top: number } | null => {
        const currentTop = -target + (mode === PlayerMode.edit ? 0 : actionsPaneSnappedCardTopPx);
        let lastTop = 0;
        let nextTop = lastTop;
        let lastMiddle = 0;

        let iIndex = 0;
        let i = cardInteractions[iIndex];
        let lastI = i;

        if (!i || iIndex > cardInteractions.length - 1) {
          return null;
        }

        while (iIndex < cardInteractions.length && i && nextTop < currentTop) {
          const height =
            (fastInteractionHeights.current?.[i.shortId] ?? 0) +
            (mode === PlayerMode.edit ? 50 : icardMarginYpx);
          lastTop = nextTop;
          lastMiddle = lastTop + height / 2;

          nextTop += height;
          lastI = i;
          i = cardInteractions[++iIndex];
        }

        lastMiddle -= icardMarginYpx;

        let newTop = nextTop;
        let newI = i;

        if (!newI || Math.abs(lastMiddle - currentTop) < Math.abs(nextTop - currentTop)) {
          logger.debug('ANIM', 'Snap scrolling to top card ', {
            lastMiddle,
            currentTop,
            nextTop,
          });
          newTop = lastTop;
          newI = lastI;
        } else {
          logger.debug('ANIM', 'Snap scrolling to bottom card', {
            lastMiddle,
            currentTop,
            nextTop,
          });
        }

        const cardHeight = fastInteractionHeights.current?.[newI.shortId] ?? 0;
        if (
          (focusedCardMode || dimensionsRef.current.actionsPane.location === 'right') &&
          snappedIdRef.current === newI.id
        ) {
          const availableHeight =
            dimensionsRef.current.actionsPane.heightPx +
            (dimensionsRef.current.actionsPane.location === 'bottom'
              ? dimensionsRef.current.actionsPane.actionsTopPx
              : -dimensionsRef.current.actionsPane.actionsTopPx) -
            actionsPaneSnappedCardTopPx -
            dimensionsRef.current.actionsPane.icard.marginYpx;
          if (cardHeight > availableHeight) {
            // don't snap if the card is taller the actions pane so the attendee can scroll to the bottom of the card
            return null;
          }
        }

        return { interaction: newI, top: -(newTop - actionsPaneSnappedCardTopPx) };
      },
      [
        mode,
        actionsPaneSnappedCardTopPx,
        cardInteractions,
        icardMarginYpx,
        fastInteractionHeights,
        focusedCardMode,
        dimensionsRef,
      ]
    );

    const snapToClosestCard = React.useCallback(
      (fromPoint?: number, timeout = 100) => {
        // Calculate current snap on the fly
        // snapPointFor has a side effect of setting the snapped interaction
        const snapResults = snapPointAndInteractionFor(fromPoint ?? scrollTop.get());

        if (!snapResults) {
          return;
        }

        // Find and set snapped which the dock will sync to
        const { interaction: newInteraction, top: newTop } = snapResults;
        setSnappedAndDebounceScrollToIcon(newInteraction);

        if (isEditAndPaused) {
          // Don't actually snap the card in edit mode
          return;
        }

        if (snapTimer.current) {
          clearTimeout(snapTimer.current);
        }
        snapTimer.current = window.setTimeout(() => {
          // Only snap to the snapped card after a debounce
          if (scrollTop.get() === newTop) {
            return;
          }

          animate(scrollTop, newTop, {
            ...(transitions['cards.snap'] as ValueAnimationTransition<number>),
          });
          snapTimer.current = 0;
        }, timeout);
      },
      [
        isEditAndPaused,
        scrollTop,
        setSnappedAndDebounceScrollToIcon,
        snapPointAndInteractionFor,
        transitions,
      ]
    );

    const hasScrollToTargetChanged = useHasChangedShallow([scrollToCardOrPositionTarget]);
    React.useEffect(() => {
      // Allows anybody with access to WebinarPlayerContext to scroll cards
      if (!hasScrollToTargetChanged || !scrollToCardOrPositionTarget || showOverlay) return;

      let target = scrollToCardOrPositionTarget;
      scrollToCardOrPosition(null); // Reset state that caused this effect to run

      logger.debug('ANIM', 'ScrollToCardTaget changed', { target });

      if (target === 'snap') {
        const nextIndex = cardInteractions.findIndex((i) => i.id === snappedIdRef.current);
        if (nextIndex === -1) {
          target = cardInteractions[0];
        } else {
          target = cardInteractions[nextIndex + 1] ?? cardInteractions[nextIndex - 1];
        }
        if (!target) {
          logger.debug('ANIM', 'WARNING. No card to target snap', {
            snappedIDRef: snappedIdRef.current,
            nextIndex,
            target,
          });
          return;
        }
      }

      // Check if target is of type number
      if (typeof target === 'number') {
        // Find last interaction card at playbackPosition from visible cards
        const cards = cardInteractions.filter(
          (i) => !i.id?.endsWith('-endsAt') && i.playbackPosition <= (target as number)
        );
        let i = InteractionCardsReverseScroll ? cards[0] : cards[cards.length - 1];
        if (!i) {
          i = cardInteractions[0];
          i = InteractionCardsReverseScroll
            ? cardInteractions[0]
            : cardInteractions[cardInteractions.length - 1];
        }
        if (!i) return;
        logger.debug('ANIM', 'Found card @ position to scroll ', {
          target,
          id: i.id,
          ids: cardInteractions.map((i) => i.id),
        });
        target = i;
      }

      scrollToCard(target, true);
      scrollToIcon(target);
    }, [
      scrollToCard,
      scrollToIcon,
      scrollToCardOrPositionTarget,
      scrollToCardOrPosition,
      cardInteractions,
      mode,
      isPlaying,
      hasScrollToTargetChanged,
      snapToClosestCard,
      scrollTop,
      showOverlay,
    ]);

    const interactionStateChanged = useHasChangedShallow([interactionState]);
    React.useEffect(() => {
      if (!interactionStateChanged) return;
      if (
        mode === PlayerMode.edit ||
        !snappedInteraction?.shortId ||
        !interactionState[snappedInteraction.shortId]?.invalid
      )
        return;
      const snappedIndex = cardInteractionsRef.current.findIndex(
        (i) => i.shortId === snappedInteraction?.shortId
      );
      const scrollTo =
        cardInteractionsRef.current[snappedIndex + 1] ??
        cardInteractionsRef.current[snappedIndex - 1];
      scrollTo && scrollToCard(scrollTo, false);
    }, [
      activeInteractionId,
      cardInteractionsRef,
      interactionState,
      interactionStateChanged,
      mode,
      scrollToCard,
      snapToClosestCard,
      snappedInteraction?.shortId,
    ]);

    const onResize = React.useCallback(() => {
      if (!refDockContainer.current) return;
      snappedInteraction && scrollDock(snappedInteraction, { duration: 0 });
    }, [snappedInteraction, scrollDock]);
    useOnWindowResize(onResize);

    // Doesn't count 'endsAt' & 'endStream'
    const totalInteractions = [...cardInteractions, ...nonCardInteractions].filter(
      (i, index, self) => {
        return index === self.findIndex((t) => t.id === i.id) && i.id && !i.id.includes('end');
      }
    ).length;

    const onClickCardEdit = React.useCallback(
      (interaction: InteractionInfo): void => {
        if (!interaction.userCanAdd()) return;

        if (isPlaybackMode) {
          return;
        }

        const pos = Math.floor(playbackPositionRef.current!);
        if (pos !== interaction.playbackPosition) {
          setPlaybackPosition(interaction.playbackPosition);
        }
        if (interaction.id?.includes('endsAt')) {
          const interactionId = interaction.id.split('-');
          if (interactionId && interactionId[0]) {
            navigate(`./${interactionId[0]}`);
          }
        } else {
          navigate(`./${interaction.id}`);
        }
      },
      [isPlaybackMode, playbackPositionRef, setPlaybackPosition]
    );

    const [isGrabbing, setIsGrabbing] = React.useState(false);

    const onDragStart = React.useCallback((_e: unknown, _info: PanInfo): void => {
      setIsGrabbing(true);
    }, []);

    const onDragEnd = React.useCallback((_e: unknown, _info: PanInfo): void => {
      setTimeout(() => {
        setIsGrabbing(false);
      }, 100);
    }, []);

    const onClickIcon = React.useCallback(
      (interaction: InteractionInfo): void => {
        setTimeout(() => {
          if (isGrabbing) {
            setIsGrabbing(false);
            return;
          }
          logger.debug('ANIM', 'ScrollToCard from OnClickIcon');
          scrollToCard(interaction, true);
          scrollToIcon(interaction);
        }, 100);
      },
      [isGrabbing, scrollToCard, scrollToIcon]
    );

    const latestInteraction = InteractionCardsReverseScroll
      ? cardInteractions[0]
      : cardInteractions[cardInteractions.length - 1];

    const hasJoinedChanged = useHasChangedShallow([
      hasJoined,
      showOverlay,
      showingInVideoFormBeforeWatching,
    ]);
    React.useEffect(() => {
      if (!hasJoinedChanged) return;

      if (
        hasJoined === HasJoined.Joined &&
        !showOverlay &&
        latestInteraction &&
        actionsPaneState === 'offscreen' &&
        !showingInVideoFormBeforeWatching
      ) {
        logger.debug('ANIM', 'Show action pane due to join');
        setTimeout(() => setActionsPaneState('shown'), 200);
      }
    }, [
      actionsPaneState,
      hasJoined,
      hasJoinedChanged,
      latestInteraction,
      setActionsPaneState,
      showOverlay,
      showingInVideoFormBeforeWatching,
    ]);

    const isCollapsedEmbeddedPlayerRef = useRefFromState(isCollapsedEmbeddedPlayer);
    const actionsPaneLocationChanged = useHasChangedShallow([dimensions.actionsPane.location]);
    React.useEffect(() => {
      // shows the actionsPane after changing from landscape to the portrait
      if (!actionsPaneLocationChanged) return;
      if (dimensions.actionsPane.location !== 'bottom') return;
      setActionsPaneState('shown');
    }, [actionsPaneLocationChanged, dimensions.actionsPane.location, setActionsPaneState]);

    const { serverDateNow } = useServerContext();
    const hasChangedCards = useHasChangedShallow([cardInteractions], {
      skipInitial: mode !== PlayerMode.edit,
    });
    const previousCardInteractions = usePrevious(cardInteractions);
    React.useLayoutEffect(() => {
      if (!hasChangedCards) return;

      if (
        hasJoined === HasJoined.Joined &&
        !showOverlay &&
        latestInteraction &&
        actionsPaneState === 'offscreen' &&
        !showingInVideoFormBeforeWatching
      ) {
        logger.debug('ANIM', '786 ActionPane -> shown');
        setTimeout(() => setActionsPaneState('shown'), 200); // delay it;
      }

      if (!latestInteraction && actionsPaneState === 'shown') {
        logger.debug('ANIM', '791 ActionPane -> offscreen');
        setActionsPaneState('offscreen');
      }

      const largeNumberOfCardChanged =
        previousCardInteractions &&
        Math.abs(cardInteractions.length - previousCardInteractions.length) > 2;
      const now = new EDate(serverDateNow(), LocalTimeZone.name);
      const activeSince = activeInteractionRef.current
        ? now.diffInSeconds(activeInteractionRef.current.lastActive)
        : undefined;

      // Only scroll to latest card if there is no other currently active card
      const skipScrollToLatest =
        isEditAndPaused ||
        !latestInteraction ||
        (activeInteractionRef.current &&
          actionsPaneState === 'shown' && // If not shown always scroll to show notification correctly
          activeSince !== undefined &&
          activeSince < staticWebinarDimensions.actionsPane.icard.inactivityTimeoutSecs);

      logger.debug('ANIM', 'SkipScroll = ', {
        skipScrollToLatest,
        isLatestNew,
        now: now.toISOString(),
        lastActive: activeInteractionRef.current?.lastActive.toISOString(),
        activeSince,
        activeInteractionRef: activeInteractionRef.current,
      });

      if (skipScrollToLatest && snappedIdRef.current) {
        // Adjust scrollTop of dock as soon as new icons appear
        const currentInteraction = cardInteractions.find((i) => i.id === snappedIdRef.current);
        if (currentInteraction) {
          // Update cursorTop to new position
          recalcDockDimensions();
        }
      }

      if (snappedIdRef.current) {
        const currentInteraction = cardInteractions.find((i) => i.id === snappedIdRef.current);
        if (currentInteraction) {
          // Update cursorTop to new position
          scrollDock(currentInteraction, { duration: 0 });
          scrollToIcon(currentInteraction, { instant: true });
        }
      }

      (async function (): Promise<void> {
        if (latestInteraction && largeNumberOfCardChanged && mode !== PlayerMode.edit) {
          // fade out all card
          logger.debug('ANIM', 'card.hide.all');
          animations.start(`card.hide.all`, { duration: 0.4, type: 'tween' }); // sometime it never finishes
          await new Promise((rs) => setTimeout(rs, 400));
          const latestInteraction = (
            InteractionCardsReverseScroll
              ? cardInteractionsRef.current[0]
              : cardInteractionsRef.current[cardInteractionsRef.current.length - 1]
          ) as InteractionInfo | undefined;

          if (latestInteraction) {
            scrollToCard(latestInteraction, false, transitions['card.new'], true);
          }
        } else if (!skipScrollToLatest) {
          // Start in parallel
          logger.debug('ANIM', '1454 scrollToCard from cardChange');
          scrollToCard(latestInteraction, false, transitions['card.new']);
        }

        if (isLatestNew) {
          // Only fly latest card in or fly in as compressed notification
          // Make sure latestInteraction.id doesn't change across these animations
          const latestInteractionId = latestInteraction.id;

          if (actionsPaneState === 'hidden' || isCollapsedEmbeddedPlayer) {
            logger.debug('ANIM', `893 start`, ['card.hide.all']);
            animations.start(`card.hide.all`);

            logger.debug('ANIM', `841 start`, [`card.compress.${latestInteractionId}`]);
            animations.start(`card.compress.${latestInteractionId}`, { duration: 0 });
          }
          logger.debug('ANIM', `844 start`, [`card.new.${latestInteractionId}`]);
          void animations.start(`card.new.${latestInteractionId}`, { duration: 0 }); // sometime it never finishes
        }

        if (isLatestNew) {
          const latestInteractionId = latestInteraction.id;

          // Wait for card.new to finish
          await new Promise((rs) => setTimeout(rs, 10));

          logger.debug('ANIM', `853 start`, [`card.show.${latestInteractionId}`]);
          await animations.start(`card.show.${latestInteractionId}`, transitions['card.new']);
        }

        if (!isCollapsedEmbeddedPlayerRef.current) {
          if (actionsPaneState === 'shown') {
            // show all cards that have appeared
            logger.debug('ANIM', `848 start`, [`card.show.all`]);

            // If we await this animation the snap takes too long
            animations.start(`card.show.all`, transitions['card.new']);
          }

          logger.debug('ANIM', `978 start`, [`icon.show.all`]);
          animations.start('icon.show.all');
        }

        if (!skipScrollToLatest) {
          // Force snap now that we finished
          setSnapped(latestInteraction);
          markInteractionInactive();
        }

        if ((actionsPaneState === 'hidden' || isCollapsedEmbeddedPlayer) && isLatestNew) {
          // Hide notification after 3 seconds if interactions are still hidden
          await animations.start(['card.notification.hide'], transitions['card.notification.hide']);
          logger.debug('ANIM', '879 start', ['card.hide.all']);

          logger.debug('ANIM', '881 start', [`card.expand.all`]);
          animations.start(`card.expand.all`, { duration: 0 });
        }
      })();
      // Was a timer due to: Need to have this happen after the card has rendered with opacity 0 so it compresses / etc before showing
    }, [
      isCollapsedEmbeddedPlayer,
      actionsPaneState,
      activeInteractionRef,
      animations,
      cardInteractions,
      isEditAndPaused,
      hasChangedCards,
      hasJoined,
      isLatestNew,
      latestInteraction,
      recalcDockDimensions,
      scrollDock,
      scrollToCard,
      scrollToIcon,
      setActionsPaneState,
      setSnapped,
      previousCardInteractions,
      mode,
      markInteractionInactive,
      cardInteractionsRef,
      serverDateNow,
      showOverlay,
      showingInVideoFormBeforeWatching,
      transitions,
      isCollapsedEmbeddedPlayerRef,
    ]);

    const isCollapsedEmbeddedPlayerChanged = useHasChangedShallow([isCollapsedEmbeddedPlayer]);
    React.useEffect(() => {
      if (!isCollapsedEmbeddedPlayerChanged) return;
      if (isCollapsedEmbeddedPlayer) {
        animations.start(`card.hide.all`, { duration: 0 });
        return;
      }
      if (actionsPaneState === 'shown') {
        animations.start(`card.expand.all`, transitions['card.new']);
        animations.start(`card.show.all`, transitions['card.new']);
      }
    }, [
      isCollapsedEmbeddedPlayer,
      actionsPaneState,
      animations,
      transitions,
      isCollapsedEmbeddedPlayerChanged,
    ]);

    const cards = React.useMemo(
      () => (
        <InteractionsStreamCards
          snappedIdRef={snappedIdRef}
          snapPointAndInteractionFor={snapPointAndInteractionFor}
          snapToClosestCard={snapToClosestCard}
          scrollToIcon={scrollToIcon}
          cardInteractions={cardInteractions}
          cardsListRef={cardsListRef}
          cardsScrollContainerRef={cardsScrollContainerRef}
          nonCardInteractions={nonCardInteractions}
          onClickCardEdit={onClickCardEdit}
          scrollToCard={scrollToCard}
          className={className}
          dataLoaded={dataLoaded}
          scrollTop={scrollTop}
          scrollingTo={scrollingTo}
        />
      ),
      [
        cardInteractions,
        className,
        dataLoaded,
        nonCardInteractions,
        onClickCardEdit,
        scrollToCard,
        scrollTop,
        scrollToIcon,
        snapPointAndInteractionFor,
        snapToClosestCard,
      ]
    );

    const idock = React.useMemo(() => {
      return (
        <>
          {false && <ScoreSpinner />}

          <motion.div
            id='idock'
            ref={refDockContainerCallback}
            css={[
              tw`flex flex-col items-center justify-center z-player-idock overflow-hidden`,
              css`
                position: absolute;

                top: 0;
                bottom: 0;
                right: ${placement === PlayerPlacement.EmbeddedPlayer && !isExpanded
                  ? '-100px'
                  : '0'};
                padding-right: ${dimensions.actionsPane.idock.paddingRightRem}rem;
                padding-top: 0;
                width: 0rem;
                border-left: 1px solid ${etheme.player.dock.border};
                background: ${etheme.player.bg /* Here so cards exit behind the dock */};
                pointer-events: all;
                scrollbar-width: 0;
                &::-webkit-scrollbar {
                  display: none; /* Safari and Chrome */
                }
              `,
            ]}
            animate={{
              y: dimensions.actionsPane.idock.top,
              width: `${dimensions.actionsPane.idock.widthRem}rem`,
            }}
            onWheel={onDockWheel}
          >
            <motion.div
              id='idock-icons'
              ref={refDockIcons}
              style={{ y: iconsTop }}
              drag='y'
              onDragStart={onDragStart}
              onDragEnd={onDragEnd}
              dragConstraints={{ top: dockBounds.top, bottom: dockBounds.bottom }}
              css={[
                tw`absolute flex flex-col items-center rounded-lg`,
                css`
                  padding: 1rem 0.6rem;
                  touch-action: none;

                  top: 0;
                  left: 0;
                  right: 0;
                `,
              ]}
            >
              <AnimatePresence>
                {cardInteractions.map((interaction, index) => (
                  <DockIcon
                    key={interaction.shortId}
                    index={index}
                    interaction={interaction}
                    onClickIcon={onClickIcon}
                    snappedIdRef={snappedIdRef}
                  />
                ))}
              </AnimatePresence>

              <motion.div
                id='idock-cursor'
                style={{
                  top: cursorTop, // Use top as transform is used in variants
                }}
                variants={{
                  'icon.show.all': { opacity: 1, scale: 1 },
                  'icon.unsnap.all': { opacity: 1, scale: 1 },
                  'cursor.hide': { scale: 0, x: '-50%', transition: transitions['icon.exit'] },
                }}
                css={[
                  tw`rounded-lg`,
                  css`
                    position: absolute;
                    z-index: 15;
                    display: flex;
                    left: 0.5rem;
                    width: ${dimensions.actionsPane.idock.widthRem - 1}rem;
                    min-height: ${dimensions.actionsPane.idock.widthRem - 1}rem;
                    opacity: 0;
                    background: ${ewebinar.actionColor};
                  `,
                ]}
              />
            </motion.div>
          </motion.div>
        </>
      );
    }, [
      refDockContainerCallback,
      placement,
      isExpanded,
      dimensions.actionsPane.idock.paddingRightRem,
      dimensions.actionsPane.idock.top,
      dimensions.actionsPane.idock.widthRem,
      etheme.player.dock.border,
      etheme.player.bg,
      onDockWheel,
      iconsTop,
      onDragStart,
      onDragEnd,
      dockBounds.top,
      dockBounds.bottom,
      cardInteractions,
      cursorTop,
      transitions,
      ewebinar.actionColor,
      onClickIcon,
    ]);

    useRunOnce(() => {
      // After load, automatically scroll to current card
      logger.debug('ANIM', 'Scroll cards to position ', playbackPositionRef?.current);
      setTimeout(() => {
        playbackPositionRef?.current && scrollCardsToPosition(playbackPositionRef?.current);
      }, 200);
    });

    if (!rooms || !ewebinar || !setPlaybackPosition || !currentRoom.caps.hasInteractions) {
      return null;
    }

    return (
      <>
        {editMode && (
          <Portal containerId='interactionsHeader'>
            <div
              className='flex-col items-center justify-center'
              css={css`
                width: ${dimensions.actionsPane.maxWidthRem}rem;
              `}
            >
              <div
                className='flex justify-center items-center text-lg pl-2e'
                css={css`
                  padding-right: ${dimensions.actionsPane.idock.widthRem}rem;
                `}
              >
                {totalInteractions.toString() || ''}
                <span>&nbsp;Interactions</span>
                <Popup
                  position='bottom center'
                  content={
                    <span>
                      Encourage attendees to participate in the experience by adding preset moments
                      of interaction throughout your eWebinar, from the waiting room through to the
                      presentation video and exit room. <br />
                      <br />
                      Add questions, special offers, polls, downloads and more...
                    </span>
                  }
                  key='Tip Attendee'
                />
              </div>
            </div>
          </Portal>
        )}
        {cards}
        <WaitFor element='iDock'>
          {idock}
          <WaitFor
            element='snapToCardOnPauseOrPlay'
            render={(): React.ReactNode => (
              <SnapToCardOnPauseOrPlay
                scrollToCard={scrollToCard}
                snappedInteraction={snappedInteraction}
              />
            )}
          />
        </WaitFor>
      </>
    );
  })
);

type ActionsPaneToggleButtonProps = {
  idockWidthRem: number;
  location: 'none' | 'bottom' | 'right';
};

export const ActionsPaneToggleButton = withContextValue<
  WebinarPlayerUIState,
  ActionsPaneToggleButtonProps,
  ActionsPaneToggleButtonProps
>(
  WebinarPlayerContext,
  ({ dimensions }) => ({
    location: dimensions.actionsPane.location,
    idockWidthRem: dimensions.actionsPane.idock.widthRem,
  }),
  'ActionsPaneToggleButton'
)(
  withWebinarPlayerContext(
    ['actionsPaneState', 'setActionsPaneState', 'isCollapsedEmbeddedPlayer', 'animations'],
    'ActionsPaneToggleButton'
  )(({ context, idockWidthRem, location }) => {
    const { actionsPaneState, setActionsPaneState, animations } = context;

    const onPaneIconClick = React.useCallback((): void => {
      setActionsPaneState(actionsPaneState === 'hidden' ? 'shown' : 'hidden');
    }, [actionsPaneState, setActionsPaneState]);

    // show or hide the ActionsPaneToggleButton when rotating the device from portrait to landscape
    const hasActionsLocationChanged = useHasChangedShallow([location], {
      skipInitial: true,
    });
    React.useEffect(() => {
      if (!hasActionsLocationChanged) return;
      if (location === 'bottom') {
        animations.start('actions.button.hide');
      }
      if (location === 'right' && actionsPaneState === 'shown') {
        animations.start('actions.button.show');
      }
    }, [actionsPaneState, animations, location, hasActionsLocationChanged]);

    logger.debug('ANIM', 'location: ', location);
    if (location !== 'right') return null;

    return (
      <motion.div
        className='ActionsPaneToggleButton'
        css={[
          tw`flex justify-center items-end z-player-chat`,
          css`
            position: absolute;
            bottom: 1.5rem;
            right: 0;
            width: ${staticWebinarDimensions.actionsPane.idock.widthRem}rem;
            ${context.isCollapsedEmbeddedPlayer ? 'display: none;' : ''}
          `,
        ]}
        style={{
          opacity: 0,
          pointerEvents: 'none',
        }}
        variants={{
          'actions.button.hide': { opacity: 0, pointerEvents: 'none' },
          'actions.button.show': { opacity: 1, pointerEvents: 'all' },

          'actions.gone': { opacity: 0, pointerEvents: 'none' },
          'actions.open': { opacity: 1, pointerEvents: 'all' },

          'actions.overlay.open': { opacity: 0, pointerEvents: 'none' },
          'actions.overlay.close': { opacity: 1, pointerEvents: 'all' },
        }}
      >
        <motion.button
          css={css`
            ${tw`flex items-center`}
          `}
          initial={{ rotateY: 0 }}
          transition={{
            duration: 0.25,
            type: 'tween',
          }}
          variants={{
            'actions.open': { rotateY: 0 },
            'actions.close': { rotateY: 180 },
          }}
          whileHover={{ scale: 1.1 }}
          onClick={onPaneIconClick}
        >
          <OpenPaneIcon
            className='text-black'
            css={css`
              width: ${idockWidthRem - 2.5}rem;
              height: ${idockWidthRem - 2.5}rem;
            `}
          />
        </motion.button>
      </motion.div>
    );
  })
);

const InteractionsStream: React.FC<{ className?: string }> = withWebinarPlayerContext(
  ['dataLoaded', 'dimensions', 'hasJoined'],
  'InteractionsStream'
)(({ context, className }) => {
  // Skip all Interaction Stream hooks if we haven't loaded all data yet
  const [mounted, setMounted] = React.useState(false);
  const { dataLoaded, dimensions, hasJoined } = context;
  const shouldNotShow = !dataLoaded || !dimensions.isMeasured || !hasJoined;

  React.useEffect(() => {
    if (!shouldNotShow) {
      setMounted(true);
    }
  }, [shouldNotShow]);
  if (shouldNotShow && !mounted) return null;

  return <InteractionsStreamLoaded className={className} />;
});

export default InteractionsStream;
